import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  MovieCreation as MovieCreationIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Share as ShareIcon,
  Assignment as AssignmentIcon,
  Launch as LaunchIcon,
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../../../../API';
import useAuth from '../../../../Hooks/use-auth';
import AddBalanceDialog from '../../../../Components/tools/addBalance';
import { getPlatformAndPromotion } from '../../../../Utils/constants';

const CampaignConfirmation = ({
  campaignData,
  selectedCreatorsData = [],
  isBlitzFeeOnCreators,
  timelinesData,
  onBack,
  onFinish,
  idealDueDate,
}) => {
  const theme = useTheme();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // FETCH BALANCE AND CREDIT INFO
  // ═══════════════════════════════════════════════════════════════════
  
  // Fetch user's current balance
  const { 
    data: balanceData, 
    isLoading: balanceLoading,
    refetch: refetchBalance
  } = useQuery(
    ["myBalance"],
    () => API.balanceLedger.fetchMyBalance(),
    {
      enabled: !!currentUser?.id,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch company info to check BlitzPay account and spending power
  const { 
    data: companyInfo,
    isLoading: companyLoading
  } = useQuery(
    ["company", currentUser?.company_id],
    () => API.companies.listFetch({ id: currentUser?.company_id }),
    { 
      enabled: !!currentUser?.company_id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch spending info if BlitzPay account
  const hasBlitzPayAccount = companyInfo?.blitzpay_account === true;

  const {
      data: spendingInfo,
      isLoading: spendingLoading
    } = useQuery(
      ["mySpendingInfo"],
      () => API.teams.getMySpendingInfo(),
      {
        enabled: !!currentUser?.id && hasBlitzPayAccount,
        staleTime: 60000,
        refetchOnWindowFocus: false,
      }
    );

  // ═══════════════════════════════════════════════════════════════════
  // CALCULATE COSTS AND AFFORDABILITY
  // ═══════════════════════════════════════════════════════════════════
  
  const projectSum = selectedCreatorsData.reduce(
    (sum, creator) => sum + (creator.adjustedPrice || 0),
    0
  );

  const blitzFee = isBlitzFeeOnCreators ? 0 : projectSum * 0.05;
  const finalCost = projectSum + blitzFee;

  const totalVideos = selectedCreatorsData.reduce((total, creator) => total + creator.videos.length, 0);
  const totalCrossposts = selectedCreatorsData.reduce((total, creator) => 
    total + creator.videos.reduce((videoTotal, video) => videoTotal + video.crossPostingKeys.size, 0), 0
  );

  const maxAffordableBudget = useMemo(() => {
    const currentBalance = parseFloat(balanceData?.balance || 0);
    
    if (hasBlitzPayAccount && spendingInfo) {
      const availableSpending = parseFloat(spendingInfo.total_available_funds || 0);
      return currentBalance + availableSpending;
    }

    return currentBalance;
  }, [balanceData, spendingInfo, hasBlitzPayAccount]);

  const canAffordCampaign = finalCost <= maxAffordableBudget;
  const isLoadingFunds = balanceLoading || companyLoading || (hasBlitzPayAccount && spendingLoading);
  const shortfall = finalCost - maxAffordableBudget;

  // ═══════════════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  const handleAddBalanceSuccess = () => {
    refetchBalance();
    setShowAddBalanceDialog(false);
  };

  const handleCreateCampaign = () => {
    setOpenDialog(false);
    onFinish();
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER AFFORDABILITY SECTION
  // ═══════════════════════════════════════════════════════════════════

  const renderAffordabilityCheck = () => {
    if (isLoadingFunds) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Verifying your available funds...
            </Typography>
          </Stack>
        </Alert>
      );
    }

    const currentBalance = parseFloat(balanceData?.balance || 0);
    const spendingAvailable = hasBlitzPayAccount ? parseFloat(spendingInfo?.total_available_funds || 0) : 0;

    if (!canAffordCampaign) {
      return (
        <Alert 
          severity="warning" 
          sx={{
            mb: 2,
            borderRadius: 3,
            alignItems: 'flex-start',
            '& .MuiAlert-message': {
              width: '100%',
            },
            '& .MuiAlert-action': {
              pt: 0.25,
              mr: 0,
              alignSelf: 'flex-start',
            },
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowAddBalanceDialog(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                flexShrink: 0,
                borderRadius: 2,
                px: 1.25,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Add ${Math.ceil(shortfall)}
            </Button>
          }
        >
          <AlertTitle sx={{ mb: 1, fontWeight: 700 }}>
            Insufficient Funds Warning
          </AlertTitle>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            This campaign costs ${finalCost.toFixed(2)} but you currently have ${maxAffordableBudget.toFixed(2)} available. 
            You can still create the campaign, but you'll need to add funds before launching it to creators.
            {hasBlitzPayAccount && spendingAvailable === 0 && ' Your BlitzPay spending power is also at $0.'}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>Available:</strong> ${maxAffordableBudget.toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Required:</strong> ${finalCost.toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Shortfall:</strong> ${shortfall.toFixed(2)}
            </Typography>
          </Stack>
        </Alert>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.success.main, 0.05),
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                bgcolor: alpha(theme.palette.success.main, 0.15),
                color: 'success.main',
              }}
            >
              <CheckCircleIcon />
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="success.main"
                sx={{ lineHeight: 1.25, mb: 0.5 }}
              >
                Sufficient Funds Available
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                You have ${maxAffordableBudget.toFixed(2)} available
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.12),
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              After Campaign
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary.main"
              sx={{ lineHeight: 1.2 }}
            >
              ${(maxAffordableBudget - finalCost).toFixed(2)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Balance: $${currentBalance.toFixed(2)}`}
              size="small"
              variant="outlined"
              color="success"
            />
            {hasBlitzPayAccount && spendingAvailable > 0 && (
              <Chip
                label={`Spending Power: $${spendingAvailable.toFixed(2)}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 200px)',
      background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
      p: 3
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        {/* Header */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
            color: 'white',
            borderRadius: 3
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CheckCircleIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  Campaign Confirmation
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Review your campaign details before creating
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Campaign Overview */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Campaign Details */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <CampaignIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Campaign Overview
                    </Typography>
                  </Stack>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Campaign Name
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {campaignData.campaignName}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Campaign Brief
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {campaignData.campaignBrief || 'No brief provided'}
                        </Typography>
                      </Paper>
                    </Box>

                    <Divider />

                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Email Recipient
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body1">
                            {campaignData.emailRecipient}
                          </Typography>
                        </Stack>
                      </Box>

                      {campaignData.manager && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Campaign Manager
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {campaignData.manager.name}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Selected Creators */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <PersonIcon color="primary" />
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Selected Creators
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedCreatorsData.length} creator{selectedCreatorsData.length !== 1 ? 's' : ''} selected
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Stack direction="row" spacing={1}>
                      <Chip
                        icon={<MovieCreationIcon />}
                        label={`${totalVideos} Videos`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {totalCrossposts > 0 && (
                        <Chip
                          icon={<ShareIcon />}
                          label={`${totalCrossposts} Crossposts`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Stack>

                  <Stack spacing={2}>
                    {selectedCreatorsData.map((creator) => (
                      <Card
                        key={creator.creator}
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', p: 2 }}>
                          <Avatar
                            src={creator.pfphref || '/default_profile.png'}
                            alt={creator.display_name}
                            sx={{ 
                              width: { xs: 60, md: 80 }, 
                              height: { xs: 60, md: 80 },
                              mr: 2
                            }}
                          />
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                              {creator.display_name}
                            </Typography>
                            
                            <Stack spacing={1}>
                              {creator.videos.map(video => {
                                const platPromo = getPlatformAndPromotion(video.rateKey);
                                return (
                                  <Box key={`${creator.creator}-${video.id}`}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                      <MovieCreationIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                      <Typography variant="body2" fontWeight={500}>
                                        {platPromo.platform} - {platPromo.promotion}
                                      </Typography>
                                    </Stack>
                                    
                                    {[...video.crossPostingKeys].map(crossPost => {
                                      const crossPostPromo = getPlatformAndPromotion(crossPost);
                                      return (
                                        <Stack 
                                          key={`${creator.creator}-${video.id}-cross-${crossPost}`}
                                          direction="row" 
                                          alignItems="center" 
                                          spacing={1}
                                          sx={{ ml: 2 }}
                                        >
                                          <ShareIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                          <Typography variant="body2" color="text.secondary">
                                            Crosspost: {crossPostPromo.platform}
                                          </Typography>
                                        </Stack>
                                      );
                                    })}
                                  </Box>
                                );
                              })}
                            </Stack>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Stack direction="row" spacing={3}>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Base Price
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  ${creator.basePrice ? parseFloat(creator.basePrice).toFixed(2) : '0.00'}
                                </Typography>
                              </Box>
                              
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Usage Rights
                                </Typography>
                                <Chip 
                                  label={creator.usageRights ? 'Included' : 'Not Included'}
                                  size="small"
                                  color={creator.usageRights ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </Box>
                              
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Final Price
                                </Typography>
                                <Typography variant="h6" color="primary.main" fontWeight={700}>
                                  ${creator.adjustedPrice ? parseFloat(creator.adjustedPrice).toFixed(2) : '0.00'}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              {/* Timeline Summary */}
              {timelinesData && Object.keys(timelinesData).length > 0 && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <ScheduleIcon color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Timeline Overview
                      </Typography>
                    </Stack>
                    
                    {Object.entries(timelinesData).map(([creatorId, events]) => (
                      <Box key={creatorId} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                          {creatorId}
                        </Typography>
                        <List dense>
                          {events.map((event, index) => (
                            <ListItem key={index} sx={{ pl: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={event.objective}
                                secondary={`Due: ${event.projected_date.toLocaleDateString()}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>

          {/* Cost Summary Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3} sx={{ position: 'sticky', top: 24 }}>
              {/* Affordability Check */}
              {renderAffordabilityCheck()}

              {/* Cost Breakdown */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <MoneyIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Cost Breakdown
                    </Typography>
                  </Stack>
                  
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1">
                        Project Sum
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        ${projectSum.toFixed(2)}
                      </Typography>
                    </Stack>
                    
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" color="text.secondary">
                        Platform Fee {!isBlitzFeeOnCreators && '(5%)'}
                      </Typography>
                      <Typography variant="body1">
                        ${blitzFee.toFixed(2)}
                      </Typography>
                    </Stack>
                    
                    <Divider />
                    
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={600}>
                        Total Cost
                      </Typography>
                      <Typography variant="h5" color="primary.main" fontWeight={700}>
                        ${finalCost.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {!isBlitzFeeOnCreators && blitzFee > 0 && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography variant="body2">
                        Platform fee (5%) will be added to your total
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Campaign Stats */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Campaign Stats
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Creators</Typography>
                      <Typography variant="body1" fontWeight={500}>{selectedCreatorsData.length}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Videos</Typography>
                      <Typography variant="body1" fontWeight={500}>{totalVideos}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Crossposts</Typography>
                      <Typography variant="body1" fontWeight={500}>{totalCrossposts}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Avg. per Creator</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        ${selectedCreatorsData.length > 0 ? (projectSum / selectedCreatorsData.length).toFixed(2) : '0.00'}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Navigation */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={onBack}
                      fullWidth
                      size="large"
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Back to Timeline
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenDialog(true)}
                      fullWidth
                      size="large"
                      disabled={isLoadingFunds}
                      startIcon={isLoadingFunds ? <CircularProgress size={16} /> : <LaunchIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {isLoadingFunds ? 'Checking Funds...' : 'Create Campaign'}
                    </Button>

                    <Button
                      variant="outlined"
                      color="success"
                      onClick={() => setShowAddBalanceDialog(true)}
                      fullWidth
                      size="large"
                      startIcon={<AddCircleIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Add Funds to Balance
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <LaunchIcon />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Ready to Launch Campaign
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Final confirmation required
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {canAffordCampaign ? (
            <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }} icon={<CheckCircleIcon />}>
              <AlertTitle>Funds Verified</AlertTitle>
              <Typography variant="body2">
                You have sufficient funds (${maxAffordableBudget.toFixed(2)}) for this ${finalCost.toFixed(2)} campaign.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }} icon={<WarningIcon />}>
              <AlertTitle>Insufficient Funds</AlertTitle>
              <Typography variant="body2">
                You need ${shortfall.toFixed(2)} more to launch this campaign. You can create it now and add funds before launching to creators.
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
            <AlertTitle>Important Note</AlertTitle>
            Your selected creators won't see this offer yet.
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            To complete the process and send offers to creators, you'll need to click the 
            <strong> "Launch Campaign"</strong> button once you're redirected to the campaign management page.
            {!canAffordCampaign && <strong> Make sure to add funds before launching.</strong>}
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            This allows you to review and make any final adjustments before creators receive their invitations.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="inherit"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCampaign}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Balance Dialog */}
      <AddBalanceDialog
        open={showAddBalanceDialog}
        onClose={() => setShowAddBalanceDialog(false)}
        onSuccess={handleAddBalanceSuccess}
      />
    </Box>
  );
};

export default CampaignConfirmation;
